import { Helmet } from "react-helmet-async";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Button } from "components/ui/button";
import { useCampaignProject } from "hooks/useCampaignProject";
import { CommonInvestButton } from "investment/CommonInvestButton";
import { validateInvestmentAmount } from "lib/investmentHelper";
import { parseOptionalQueryNumber, parseQueryNumber } from "lib/queryParams";

export default function InvestPage() {
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const { campaign, loading: campaignLoading, error: campaignError } =
    useCampaignProject(campaignId);

  const amountParam = searchParams.get("amount") ?? "";
  const amount = Number(amountParam);
  const minParam = searchParams.get("min");
  const maxParam = searchParams.get("max");
  const minInvestment =
    parseQueryNumber(minParam, 0) || campaign?.minInvestment || 0;
  const maxInvestment =
    parseOptionalQueryNumber(maxParam) ?? campaign?.maxInvestment;

  const validationError = validateInvestmentAmount(amount, {
    min: minInvestment,
    max: maxInvestment,
  });

  const checkoutBlocked =
    Boolean(validationError) || campaignLoading || Boolean(campaignError);

  const formattedAmount = Number.isFinite(amount)
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount)
    : null;

  return (
    <>
      <Helmet>
        <title>Invest | Fortify</title>
      </Helmet>
      <div className="container mx-auto max-w-lg px-4 py-16 text-center sm:px-6 lg:px-12 xl:px-16">
        <p className="text-sm text-muted-foreground uppercase tracking-wide">
          Step 3 of 3
        </p>
        <h1 className="text-2xl font-semibold mt-1">Confirm investment</h1>
        <p className="mt-4 text-muted-foreground">
          Review your amount, then continue to our secure Stripe checkout.
        </p>

        {formattedAmount ? (
          <p className="mt-6 text-3xl font-bold tabular-nums">{formattedAmount}</p>
        ) : (
          <p className="mt-4 text-sm text-destructive">
            Missing or invalid amount. Return to the campaign and choose an investment amount.
          </p>
        )}

        {validationError ? (
          <p className="mt-2 text-sm text-destructive">{validationError}</p>
        ) : null}

        {campaignError ? (
          <p className="mt-2 text-sm text-destructive">{campaignError}</p>
        ) : null}

        <div className="mt-8 space-y-3">
          <CommonInvestButton
            variant="checkout"
            projectId={campaignId}
            amount={amount}
            minInvestment={minInvestment}
            maxInvestment={maxInvestment}
            campaignStartAt={campaign?.campaignStartAt}
            campaignEndAt={campaign?.campaignEndAt}
            daysRemaining={campaign?.daysRemaining}
            status={campaign?.status}
            canInvest={campaign?.canInvest}
            isSample={campaign?.isSample}
            disabled={checkoutBlocked}
            label={campaignLoading ? "Loading campaign…" : undefined}
          />
          {campaignId ? (
            <Button variant="outline" className="w-full" asChild>
              <Link to={`/campaign/${campaignId}`}>Back to campaign</Link>
            </Button>
          ) : null}
          <Button variant="ghost" className="w-full" asChild>
            <Link to="/dashboard">Go to dashboard</Link>
          </Button>
        </div>

        <p className="mt-6 text-xs text-muted-foreground">
          Payments are processed securely by Stripe. After checkout you will be redirected to
          confirm your investment.
        </p>
      </div>
    </>
  );
}
